import i18n from 'utils/I18n';

import { CouponAPIHelper } from 'services/CouponAPIHelper';

import { createAction, delay, getTranslationData } from 'utils';
import { subHeaderLink } from 'utils/CampaignUtil';
import {
  COUPON_FORMATS,
  COUPON_REDEMPTION_METHOD,
  COUPON_TEMPLATE_TYPE,
  CouponTemplateType,
} from 'utils/CustomEnums';
import {
  convertDateToStamp,
  getCouponDetailExpireDate,
  getCouponEffectiveDate,
  getCouponListExpireDate,
  getCurrentDate,
  getLeftDays,
} from 'utils/DateTimeUtils';

// const COUPON_EXPIRE_DATE_FIXED_TIME = "T23:59:59+08:00";
export const USE_COUPON_STATUS = {
  TYPING: 0,
  SUCCESS: 1,
  FAILED: 2,
  NO_NETWORK: 3,
  INVALID: 4,
  BLACKOUT: 5,
  USED: 6,
  INACTIVE: 7,
  EXPIRED: 8,
  UNAVAIABLE: 9,
  STORE_ERROR: 10,
};
export const COUPON_STATUS_INFO = {
  [USE_COUPON_STATUS.SUCCESS]: {
    titleKey: 'coupon:useSuccessTitle',
    contentKey: 'coupon:useSuccessContent',
    ticketContentKey: 'coupon:ticketUseSuccessContent',
  },
  [USE_COUPON_STATUS.FAILED]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:useFailedContent',
    ticketContentKey: 'coupon:useFailedContent',
  },
  [USE_COUPON_STATUS.USED]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:couponIsUsed',
    ticketContentKey: 'coupon:ticketIsUsed',
  },
  [USE_COUPON_STATUS.INACTIVE]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:couponIsInactive',
    ticketContentKey: 'coupon:ticketIsInactive',
  },
  [USE_COUPON_STATUS.EXPIRED]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:couponIsExpired',
    ticketContentKey: 'coupon:ticketIsExpired',
  },
  [USE_COUPON_STATUS.UNAVAIABLE]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:couponIsUnavaiable',
    ticketContentKey: 'coupon:ticketIsUnavaiable',
  },
  [USE_COUPON_STATUS.BLACKOUT]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:useFailedBlackoutContent',
    ticketContentKey: 'coupon:ticketUseFailedBlackoutContent',
  },
  [USE_COUPON_STATUS.STORE_ERROR]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:storeError',
    ticketContentKey: 'coupon:ticketStoreError',
  },
  [USE_COUPON_STATUS.NO_NETWORK]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'ticket_no_internet_connection',
    ticketContentKey: 'coupon:ticket_no_internet_connection',
  },
  [USE_COUPON_STATUS.INVALID]: {
    titleKey: 'coupon:useFailedTitle',
    contentKey: 'coupon:useFailedContent',
    ticketContentKey: 'coupon:useFailedContent',
  },
};
const COUPON_USE_RESULT = [
  {
    statusKey: USE_COUPON_STATUS.INVALID,
    errorDetail: 'Invalid staff code',
  },
  {
    statusKey: USE_COUPON_STATUS.USED,
    errorDetail: 'Coupon is used.',
  },
  {
    statusKey: USE_COUPON_STATUS.INACTIVE,
    errorDetail: 'Coupon has been disabled',
  },
  {
    statusKey: USE_COUPON_STATUS.BLACKOUT,
    errorDetail: 'The Coupon cannot be used today.',
  },
  {
    statusKey: USE_COUPON_STATUS.EXPIRED,
    errorDetail: 'Coupon is expired.',
  },
  {
    statusKey: USE_COUPON_STATUS.UNAVAIABLE,
    errorDetail: 'Coupon is not effective yet',
  },
  {
    statusKey: USE_COUPON_STATUS.STORE_ERROR,
    errorDetail: 'Invalid store QR code',
  },
];
const getCardData = (coupon, now) => {
  const effectiveDate = convertDateToStamp(coupon.effective_date);
  const expiryDate = convertDateToStamp(
    coupon.expiry_date
      ? // ? `${coupon.expiry_date}${COUPON_EXPIRE_DATE_FIXED_TIME}`
        `${coupon.expiry_date}`
      : '',
  );
  const redemptionDate = convertDateToStamp(
    coupon.date_of_earning || coupon.date_of_purchase || coupon.date_of_grant,
  );
  const translatedData = getTranslationData(coupon.template);
  const brandData = getBrand(coupon);

  const data = {
    id: coupon.id,
    isTicket: coupon.template.coupon_type === CouponTemplateType.TICKET,
    coverPhotoUrl: translatedData.cover_photo_url,
    name: translatedData.name,
    brandIconUrl: brandData?.iconUrl,
    isNew: now - redemptionDate < 3600 * 1000 * 24,
    isUsed: coupon.is_used,
    unavailable: effectiveDate === null ? false : now < effectiveDate,
    displayDate:
      effectiveDate && now < effectiveDate
        ? getCouponEffectiveDate(effectiveDate)
        : getCouponListExpireDate(now, expiryDate),
    lessThan6Days:
      expiryDate === null ? false : getLeftDays(now, expiryDate) <= 6,
    isExpired: coupon.is_expired,
    ...getCouponOMOData(coupon),
  };
  return data;
};

const getStore = (coupon) => {
  const shown = 'store' in coupon && coupon.store !== null;
  if (!shown) {
    return {
      shown: false,
    };
  }
  const baseData = getTranslationData(coupon.store);
  return {
    shown: true,
    id: baseData.id,
    name: baseData.name,
    address: baseData.address,
    isOnlineStore: baseData?.is_online_store,
  };
};

const getBrand = (coupon) => {
  const { brand, related_brands: relatedBrands } = coupon.template;
  let brandData = null;
  if (relatedBrands && relatedBrands.length === 1) {
    brandData = relatedBrands[0];
  } else if (brand) {
    brandData = brand;
  }
  if (brandData === null) {
    return { shown: false };
  }
  const baseData = getTranslationData(brandData);
  return {
    shown: true,
    iconUrl: brandData.icon_url,
    name: baseData.name,
    description: baseData.description,
  };
};

const getSerialNumber = (coupon) => {
  const brandID = coupon.template.brand ? coupon.template.brand.id : 0;
  const storeID = coupon.store ? coupon.store.id : 0;
  return `${coupon.template.id}-${brandID}-${storeID}-${coupon.id}`;
};

const getCouponOMOData = (coupon) => {
  let usageMedium = coupon.usage_medium;
  const userSelectedUsageMedium = coupon.user_selected_usage_medium;
  if (
    usageMedium === 'NOT_LIMIT' &&
    userSelectedUsageMedium !== 'NOT_SELECT' &&
    coupon?.template?.should_select_usage_medium
  ) {
    usageMedium = userSelectedUsageMedium;
  }
  const templateMediumType = coupon?.template?.medium_type;
  if (
    usageMedium === 'NOT_LIMIT' &&
    ['ONLINE', 'OFFLINE'].includes(templateMediumType)
  ) {
    usageMedium = templateMediumType;
  }
  let lookupFieldPrefix = '';
  if (usageMedium === 'ONLINE') {
    lookupFieldPrefix = 'online_';
  } else if (usageMedium === 'OFFLINE') {
    lookupFieldPrefix = 'offline_';
  }
  const isVirtualCoupon =
    coupon?.virtual_real_type === 'VIRTUAL' && coupon?.real_coupon === null;
  const shouldSelectStore =
    isVirtualCoupon && coupon?.template?.should_use_in_store;
  const shouldSelectOnlineOrOffline =
    isVirtualCoupon &&
    !shouldSelectStore &&
    coupon?.template?.show_online_offline_selection;
  const shouldCallSelectUsageMedium =
    shouldSelectOnlineOrOffline && coupon?.template?.should_select_usage_medium;
  return {
    usageMedium,
    lookupFieldPrefix,
    isVirtualCoupon,
    shouldSelectStore,
    shouldSelectOnlineOrOffline,
    shouldCallSelectUsageMedium,
  };
};

const getCouponDetailData = (coupon, now) => {
  const expiryDate = convertDateToStamp(coupon.expiry_date);
  const baseData = getTranslationData(coupon.template);
  const hasDetail = (baseData.detail_section_content || '').length > 0;
  const hasInstruction =
    (baseData.instruction_section_content || '').length > 0;
  const { lookupFieldPrefix, ...others } = getCouponOMOData(coupon);
  const redemptionMethod =
    coupon?.template?.[`${lookupFieldPrefix}redemption_method`];
  const couponTemplateType =
    coupon?.template?.[`${lookupFieldPrefix}coupon_template_type`];
  const inBlackoutPeriod = coupon?.template?.is_within_blackout_period;
  const isExpired = coupon.is_expired;
  const isUsed = coupon.is_used;
  const showMerchantScanQRCode =
    redemptionMethod ===
      COUPON_REDEMPTION_METHOD.MERCHANT_APP_SCAN_COUPON_QR_CODE &&
    !isUsed &&
    !isExpired;
  const couponCodeFormat = coupon?.template?.[`${lookupFieldPrefix}formats`];
  let showScanableCouponCode = false;
  let showCopiableString =
    coupon?.template?.[`${lookupFieldPrefix}show_copiable_string`];
  if (couponTemplateType === COUPON_TEMPLATE_TYPE.IMPORT_EXISTING_COUPON) {
    if (redemptionMethod === COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE) {
      showScanableCouponCode = couponCodeFormat !== COUPON_FORMATS.TEXT;
    } else if (
      isUsed &&
      redemptionMethod !==
        COUPON_REDEMPTION_METHOD.MERCHANT_APP_SCAN_COUPON_QR_CODE
    ) {
      showScanableCouponCode = couponCodeFormat !== COUPON_FORMATS.TEXT;
    }
    if (lookupFieldPrefix === 'online_') {
      showCopiableString = true;
    }
  }

  const isTicket = coupon?.template?.coupon_type === CouponTemplateType.TICKET;

  const data = {
    id: coupon.id,
    base: {
      isTicket,
      images: baseData?.photos_url || [],
      name: baseData.name,
      serialNumber: coupon.serial_number,
      displayDate: getCouponDetailExpireDate(now, expiryDate, isTicket),
      code: coupon?.code,
      isUsed,
      isExpired,
      redemptionMethod,
      couponTemplateType,
      inBlackoutPeriod,
      showMerchantScanQRCode,
      showScanableCouponCode,
      couponCodeFormat,
      showCopiableString,
      barcodeFormat: coupon?.template?.[`${lookupFieldPrefix}barcode_format`],
      ...others,
    },
    store: getStore(coupon),
    brand: getBrand(coupon),
    instruction: {
      shown: hasInstruction,
      title:
        baseData.instruction_section_title || i18n.t('campaignDetail:howToUse'),
      content: baseData.instruction_section_content,
    },
    detail: {
      shown: hasDetail,
      title: baseData.detail_section_title || i18n.t('campaignDetail:details'),
      content: baseData.detail_section_content,
    },
  };
  return data;
};

const processCouponCardData = (item) => {
  const now = getCurrentDate();
  return {
    original: { ...item },
    card: getCardData(item, now),
  };
};

const processCouponDetailData = (coupon) => {
  const now = getCurrentDate();
  return getCouponDetailData(coupon, now);
};

const processCouponList = (couponList) => {
  const processedData = [];
  couponList.forEach((item, index) => {
    if (!item.is_forced_inactive) {
      processedData.push(processCouponCardData(item));
    }
  });
  return processedData;
};

export const getCouponOrTicketNameMap = (isTicket) => {
  let myListName = 'MyCoupon';
  let listStateName = 'coupons';
  let nextLinkStateName = 'nextLink';
  let currentTabIndexStateName = 'currentTabIndex';

  if (isTicket) {
    myListName = 'MyTicket';
    listStateName = 'tickets';
    nextLinkStateName = 'ticketNextLink';
    currentTabIndexStateName = 'ticketCurrentTabIndex';
  }
  return {
    myListName,
    listStateName,
    nextLinkStateName,
    currentTabIndexStateName,
  };
};

export default {
  namespace: 'myCoupon',
  state: {
    coupons: [],
    nextLink: null,
    currentTabIndex: 0,
    detailDataMap: {},
    couponApplicableStoreMap: {},
    useCouponStatus: USE_COUPON_STATUS.TYPING, // 0: typing, 1: success, 2:falied, 3: no network, 4: invalid
    selectedTestCase: null,
    selectedStore: {},
    tickets: [],
    ticketNextLink: null,
    ticketCurrentTabIndex: 0,
  },
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    addDetailData(state, { payload }) {
      const { couponID, data } = payload;
      return {
        ...state,
        detailDataMap: { ...state.detailDataMap, [couponID]: data },
      };
    },
    addCouponStores(state, { payload }) {
      const { couponID, stores } = payload;
      return {
        ...state,
        couponApplicableStoreMap: {
          ...state.couponApplicableStoreMap,
          [couponID]: stores,
        },
      };
    },
    resetState(state, { payload }) {
      return {
        coupons: [],
        currentTabIndex: 0,
        detailDataMap: {},
        useCouponStatus: USE_COUPON_STATUS.TYPING,
        tickets: [],
        ticketNextLink: null,
        ticketCurrentTabIndex: 0,
      };
    },
    updateUseCouponStatus(state, { payload }) {
      return {
        ...state,
        useCouponStatus: payload.status,
      };
    },
    selectStore(state, { payload }) {
      return {
        ...state,
        selectedStore: payload,
      };
    },
  },
  effects: {
    refreshMyCoupon: [
      function* ({ payload = {} }, { select, call, put }) {
        const { isTicket } = payload;

        const nameMap = getCouponOrTicketNameMap(isTicket);
        const { currentTabIndex, selectedTestCase, customerID } = yield select(
          (state) => ({
            currentTabIndex: state.myCoupon[nameMap.currentTabIndexStateName],
            selectedTestCase: state.myCoupon.selectedTestCase,
            customerID: state.users.userInfo.uid,
          }),
        );
        const startTime = Date.now();
        yield put(
          createAction('pullrefreshandloadmore/updateState')({
            [`${nameMap.myListName}IsRefreshing`]: true,
            [`${nameMap.myListName}HasMoreData`]: true,
          }),
        );
        const response = yield call(CouponAPIHelper.getMyCoupon, {
          testCaseID: selectedTestCase,
          tabIndex: currentTabIndex,
          customerID,
          isTicket,
        });
        const endTime = Date.now();
        if (endTime - startTime < 1500) {
          yield call(delay, 1500 - endTime + startTime);
        }
        yield put(
          createAction('pullrefreshandloadmore/updateState')({
            [`${nameMap.myListName}IsRefreshing`]: false,
          }),
        );
        if (response.status === 200) {
          const { data, headers } = response;
          const nextLink = subHeaderLink(headers.link);
          yield put(
            createAction('pullrefreshandloadmore/updateState')({
              [`${nameMap.myListName}HasMoreData`]: !!nextLink,
            }),
          );

          yield put(
            createAction('updateState')({
              [nameMap.listStateName]: processCouponList(data),
              [nameMap.nextLinkStateName]: nextLink,
            }),
          );
        } else {
          yield put(
            createAction('pullrefreshandloadmore/updateState')({
              [`${nameMap.myListName}HasMoreData`]: false,
            }),
          );
        }
      },
    ],

    loadMoreCoupon: [
      function* ({ payload = {} }, { select, put, call }) {
        const { isTicket } = payload;

        const nameMap = getCouponOrTicketNameMap(isTicket);
        const {
          currentTabIndex,
          currentCoupons,
          customerID,
          nextLink,
          selectedTestCase,
        } = yield select((state) => ({
          currentTabIndex: state.myCoupon[nameMap.currentTabIndexStateName],
          currentCoupons: state.myCoupon[nameMap.listStateName],
          customerID: state.users.userInfo.uid,
          nextLink: state.myCoupon[nameMap.nextLinkStateName],
          selectedTestCase: state.myCoupon.selectedTestCase,
        }));
        if (nextLink === null || nextLink === '') {
          return;
        }
        const startTime = Date.now();
        const response = yield call(CouponAPIHelper.getMyCoupon, {
          testCaseID: selectedTestCase,
          tabIndex: currentTabIndex,
          customerID,
          pageLink: nextLink,
          isTicket,
        });
        const endTime = Date.now();
        if (endTime - startTime < 1000) {
          yield call(delay, 1000 - endTime + startTime);
        }
        yield put(
          createAction('pullrefreshandloadmore/updateState')({
            [`${nameMap.myListName}IsLoadingMore`]: false,
          }),
        );
        if (response.status === 200) {
          const { data, headers } = response;
          const nextLink = subHeaderLink(headers.link);
          yield put(
            createAction('pullrefreshandloadmore/updateState')({
              [`${nameMap.myListName}HasMoreData`]: !!nextLink,
            }),
          );

          const coupons = [...currentCoupons, ...processCouponList(data)];
          yield put(
            createAction('updateState')({
              [nameMap.listStateName]: coupons,
              [nameMap.nextLinkStateName]: nextLink,
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],
    changeTab: [
      function* ({ payload = {} }, { select, put }) {
        const { index, isTicket } = payload;

        const nameMap = getCouponOrTicketNameMap(isTicket);
        const currentTabIndex = yield select(
          (state) => state.myCoupon[nameMap.currentTabIndexStateName],
        );
        if (currentTabIndex === index) {
          return;
        }
        yield put(
          createAction('updateState')({
            [nameMap.listStateName]: [],
            [nameMap.currentTabIndexStateName]: index,
            [nameMap.nextLinkStateName]: null,
          }),
        );
        yield put(
          createAction('myCoupon/refreshMyCoupon')({
            isTicket,
          })
        );
      },
      { type: 'takeLatest' },
    ],

    prepareDetailData: [
      function* ({ payload }, { select, put, call }) {
        // using list data first, then fetch detail data from coupon detail api
        const { couponID, isTicket } = payload;
        const nameMap = getCouponOrTicketNameMap(isTicket);

        const { customerID, currentCoupons, selectedTestCase } = yield select(
          (state) => ({
            customerID: state.users.userInfo.uid,
            currentCoupons: state.myCoupon[nameMap.listStateName],
            selectedTestCase: state.myCoupon.selectedTestCase,
          }),
        );

        const originCoupon = currentCoupons?.find(
          (item, index) => item.original.id === couponID,
        )?.original;
        if (originCoupon) {
          const coupon = processCouponDetailData(originCoupon);
          // yield put(createAction("addDetailData")({ couponID, data: coupon }));
          yield put({
            type: 'addDetailData',
            payload: { couponID, data: coupon },
          });
        }

        const response = yield call(CouponAPIHelper.getCouponDetail, {
          testCaseID: selectedTestCase,
          customerID,
          couponID,
          isTicket,
        });
        if (response.status === 200) {
          const coupon = processCouponDetailData(response.data);
          // yield put(createAction("addDetailData")({ couponID, data: coupon }));
          yield put({
            type: 'addDetailData',
            payload: { couponID, data: coupon },
          });
        }
      },
      { type: 'takeEvery' },
    ],
    getCouponApplicableStores: [
      function* ({ payload }, { select, put, call }) {
        const { couponID } = payload;
        const customerID = yield select((state) => state.users.userInfo.uid);
        yield put(
          createAction('pullrefreshandloadmore/updateState')({
            couponApplicableStoreIsLoading: true,
          }),
        );
        const response = yield call(
          CouponAPIHelper.getCouponApplicableStores,
          customerID,
          couponID,
        );

        if (response.status === 200) {
          const { data } = response;
          yield put({
            type: 'addCouponStores',
            payload: { couponID, stores: data },
          });
        }
        yield put(
          createAction('pullrefreshandloadmore/updateState')({
            couponApplicableStoreIsLoading: false,
          }),
        );
      },
      { type: 'takeLatest' },
    ],
    convertVirtualToReal: [
      function* ({ payload }, { select, put, call }) {
        const { couponID, storeID, usageMedium, afterSuccessAction } = payload;
        const response = yield call(
          CouponAPIHelper.convertCouponVirtualToReal,
          couponID,
          storeID,
          usageMedium,
        );
        if (response.status < 300) {
          const { data } = response;
          afterSuccessAction && afterSuccessAction(data);
        } else {
          let errorMessage = response.response?.data?.detail;
          if (
            errorMessage ===
            "Oops, coupon is out of stock. Let's wait until next time!"
          ) {
            errorMessage = i18n.t('campaignDetail:couponOutOfStock');
          }
          yield put({
            type: 'navBars/updateState',
            payload: {
              toastShowing: {
                value: true,
                content: errorMessage,
              },
            },
          });
        }
      },
      { type: 'takeLatest' },
    ],
    useCoupon: [
      function* ({ payload }, { put, call }) {
        const { couponID, code, qrCode } = payload;
        let result = null;
        try {
          const response = yield call(
            CouponAPIHelper.useCoupon,
            couponID,
            code,
            qrCode,
          );

          const { status } = response;
          if (status < 300) {
            result = USE_COUPON_STATUS.SUCCESS;
          }
        } catch (error) {
          if ('response' in error) {
            const errorDetail = error.response.data?.detail;
            const statusKey = COUPON_USE_RESULT.find(
              (item) => item.errorDetail === errorDetail,
            )?.statusKey;
            result = statusKey || USE_COUPON_STATUS.FAILED;
          } else {
            result = USE_COUPON_STATUS.FAILED;
          }
        }
        if (result !== null) {
          yield put(
            createAction('updateUseCouponStatus')({
              status: result,
            }),
          );
        }
        const afterActions = payload.afterActions || (() => {});
        yield afterActions();
      },
      { type: 'takeLatest' },
    ],
  },
};
